import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";

import BrowserNotificationSystemMessage from "./messages/BrowserNotificationSystemMessage";
import ConfirmationMessageContainer from "./ConfirmationMessageContainer";
import { message as messagePropType } from "./ChatPropTypes";
import MessageContainer from "./MessageContainer";
import MessageType from "../../constants/ChatMessageTypes";
import ChatActions from "../../actions/ChatActions";
import ResponseMessage from "./messages/ResponseMessage";
import { isScrolledToBottom } from "../../utils/Utils";
import AutoResponseMessageButton from "./messages/AutoResponseMessageButton";
class MessageList extends Component {
    constructor() {
        super();

        this.state = {
            animationDirection: "to-right",
            htmlUrl: null,
        };
        this.isScrolledToBottom = true;

        this.markResponseAsRead = this.markResponseAsRead.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.getUrl = this.getUrl.bind(this);
    }

    componentDidMount() {
        if (!isEmpty(this.props.response)) {
            this.markResponseAsRead(this.props.response.id);
        }
        this.props.sendMessageListRefToParent(this.messageListRef);
    }

    componentWillReceiveProps(nextProps) {
        const direction = nextProps.responseIndex > this.props.responseIndex ? "to-left" : "to-right";

        if (nextProps.response.id !== this.props.response.id) {
            this.markResponseAsRead(nextProps.response.id);
        }
        this.setState({ animationDirection: direction });
    }

    componentDidUpdate(prevProps) {
        this.props.sendMessageListRefToParent(this.messageListRef);
        if (prevProps.response.id !== this.props.response.id) {
            ChatActions.refreshSystemMessages.defer();
            this.props.scrollToBottom();
        }
        if (this.isScrolledToBottom) {
            this.props.scrollToBottom();
        }
    }

    onScroll(event) {
        this.isScrolledToBottom = isScrolledToBottom(event.target);
    }

    markResponseAsRead(responseId) {
        ChatActions.markResponseAsRead.defer({
            questionUuid: this.props.question.uuid,
            responseId,
        });
    }
    getUrl(url) {
        this.setState({ htmlUrl: url });
        const element = document.getElementsByClassName("thread-view-footer")[0];
        if (element) {
            element.classList.add("hide-footer");
        }
    }
    render() {
        const {
            autoRateAcceptedAnswers,
            confirmationMessageType,
            disableAskNewQuestionFromUi,
            footerHeight,
            messages,
            question,
            response,
            shouldDisplayBrowserNotificationPrompt,
            shouldDisplayConfirmationMessage,
            messageAlignment,
        } = this.props;
        const { animationDirection, htmlUrl } = this.state;
        const transitionName = `slide-${animationDirection}`;

        return (
            <ReactCSSTransitionGroup
                className="message-list-transition-wrapper"
                component="div"
                style={{ bottom: footerHeight }}
                transitionEnterTimeout={160}
                transitionLeaveTimeout={160}
                transitionName={transitionName}
            >
                {htmlUrl != null && (
                    <div className="iframe-container">
                        <iframe
                            title="Code Assist"
                            src={htmlUrl}
                            width="100%"
                            height="580px"
                            style={{ border: "none" }}
                            allowFullScreen
                        />
                    </div>
                )}
                <div
                    className="message-list-transition-item"
                    key={this.props.response.id}
                    onScroll={this.onScroll}
                    ref={element => {
                        this.messageListRef = element;
                    }}
                    style={htmlUrl != null ? { display: "none" } : {}}
                >
                    <div className="message-list-wrapper">
                        <ul
                            className={cx("message-list", {
                                "top-aligned": messageAlignment === "top",
                            })}
                        >
                            <ResponseMessage
                                author={question.author}
                                dateCreated={question.dateCreated}
                                id={question.uuid}
                                isFirstResponseMessage={false}
                                isLoading={false}
                                messageText={question.body}
                                type={MessageType.Question}
                            />
                            {shouldDisplayBrowserNotificationPrompt && <BrowserNotificationSystemMessage />}
                            {messages.map(message => (
                                <div key={message.id}>
                                    {message.type === MessageType.AutoResponseMessage &&
                                    message.messageTextHtml.includes("button::") ? (
                                        <div className="auto-response" key={message.id}>
                                            <AutoResponseMessageButton
                                                author={message.author}
                                                companyName={question.company}
                                                responseMessageAuthor={response.responseMessageAuthor}
                                                dateCreated={message.dateCreated}
                                                id={message.id}
                                                messageTextHtml={message.messageTextHtml}
                                                showChatbotNameAndAvatar={response.showChatbotNameAndAvatar}
                                                viewedByUser={message.viewedByUser}
                                                messageType={message.type}
                                                getUrl={this.getUrl}
                                                questionUuid={question.uuid}
                                                authorUuid={question.author.uuid}
                                            />
                                        </div>
                                    ) : (
                                        <MessageContainer
                                            autoRateAcceptedAnswers={autoRateAcceptedAnswers}
                                            disableAskNewQuestionFromUi={disableAskNewQuestionFromUi}
                                            key={message.id}
                                            message={message}
                                            question={question}
                                            response={response}
                                            scrollToBottom={this.props.scrollToBottom}
                                        />
                                    )}
                                </div>
                            ))}
                            {shouldDisplayConfirmationMessage && (
                                <ConfirmationMessageContainer
                                    messageType={confirmationMessageType}
                                    response={response}
                                />
                            )}
                        </ul>
                    </div>
                </div>
            </ReactCSSTransitionGroup>
        );
    }
}

MessageList.defaultProps = {
    confirmationMessageType: undefined,
    messageAlignment: "bottom",
    question: {
        author: {},
        dateCreated: undefined,
        body: undefined,
        uuid: undefined,
    },
    response: {
        id: undefined,
    },
};

MessageList.propTypes = {
    autoRateAcceptedAnswers: PropTypes.bool.isRequired,
    confirmationMessageType: PropTypes.oneOf([
        MessageType.AcceptResponseConfirmationMessage,
        MessageType.AutoResponseConfirmationMessage,
        MessageType.MarkAsResolvedMessage,
    ]),
    disableAskNewQuestionFromUi: PropTypes.bool.isRequired,
    footerHeight: PropTypes.number.isRequired,
    messages: PropTypes.arrayOf(messagePropType).isRequired,
    question: PropTypes.shape({
        author: PropTypes.shape({}),
        dateCreated: PropTypes.string,
        body: PropTypes.string,
        uuid: PropTypes.string,
    }),
    response: PropTypes.shape({
        id: PropTypes.number,
    }),
    responseIndex: PropTypes.number.isRequired,
    scrollToBottom: PropTypes.func.isRequired,
    sendMessageListRefToParent: PropTypes.func.isRequired,
    shouldDisplayBrowserNotificationPrompt: PropTypes.bool.isRequired,
    shouldDisplayConfirmationMessage: PropTypes.bool.isRequired,
    messageAlignment: PropTypes.oneOf(["top", "bottom"]),
};

export default MessageList;
